"use client";

import * as React from "react";
import { ChevronDownIcon } from "lucide-react";
import { Popover as PopoverPrimitive } from "radix-ui";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface TagMultiSelectProps {
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function TagMultiSelect({
  options,
  value,
  onChange,
  placeholder = "全部标签",
  className,
}: TagMultiSelectProps) {
  const label =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? value[0]
        : `已选 ${value.length} 项`;

  function toggle(tag: string, checked: boolean) {
    if (checked) onChange([...value, tag]);
    else onChange(value.filter((t) => t !== tag));
  }

  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger
        className={cn(
          "border-input data-[placeholder]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 dark:hover:bg-input/50 flex h-8 w-32 items-center justify-between gap-2 rounded-md border bg-transparent px-3 text-xs whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
          value.length === 0 && "text-muted-foreground",
          className,
        )}
      >
        <span className="line-clamp-1">{label}</span>
        <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="bg-popover text-popover-foreground z-50 min-w-[var(--radix-popover-trigger-width)] rounded-md border p-1 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          {options.length > 0 && (
            <>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => onChange(options)}
                  disabled={value.length === options.length}
                  className="hover:bg-accent hover:text-accent-foreground flex-1 rounded-sm px-2 py-1.5 text-left text-xs disabled:cursor-not-allowed disabled:opacity-50"
                >
                  全选
                </button>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  disabled={value.length === 0}
                  className="hover:bg-accent hover:text-accent-foreground flex-1 rounded-sm px-2 py-1.5 text-left text-xs disabled:cursor-not-allowed disabled:opacity-50"
                >
                  清空
                </button>
              </div>
              <div className="bg-border my-1 h-px" />
            </>
          )}
          <div className="max-h-60 overflow-y-auto">
            {options.length === 0 ? (
              <div className="text-muted-foreground px-2 py-1.5 text-xs">
                暂无标签
              </div>
            ) : (
              options.map((opt) => {
                const checked = value.includes(opt);
                return (
                  <label
                    key={opt}
                    className="hover:bg-accent hover:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(c) => toggle(opt, c === true)}
                    />
                    <span className="line-clamp-1">{opt}</span>
                  </label>
                );
              })
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
